/**
 * Trusted Types (design §10.1, threat model T04): with the CSP's
 * `require-trusted-types-for 'script'`, a string reaches a script sink only
 * through this default policy. Inline script is never built from strings;
 * HTML (MapLibre's credits, scale and popups) is cut down to text, links and
 * simple formatting; script URLs (MapLibre's worker, the AGC worklet) must
 * be this page's or blob: URLs. Imported first, before any worker is
 * created. Browsers without Trusted Types ignore the directive.
 *
 * @module
 */

interface Policy {
  createHTML(input: string): unknown;
}
interface TrustedTypesFactory {
  createPolicy(
    name: string,
    rules: {
      createHTML: (s: string) => string;
      createScript: (s: string) => string;
      createScriptURL: (s: string) => string;
    },
  ): Policy;
}

const refuse = (what: string) => (): string => {
  throw new TypeError(`Trusted Types: ${what} from a string is not allowed`);
};

/**
 * Same scheme and host as `page` (not `origin`: the desktop app's `avgv://`
 * pages have an opaque one), or a blob: URL, which only this page's own
 * blobs can be.
 */
export function scriptUrlAllowed(url: string, page: string): boolean {
  try {
    const here = new URL(page);
    const u = new URL(url, page);
    return (
      u.protocol === "blob:" ||
      u.href.startsWith(`${here.protocol}//${here.host}/`)
    );
  } catch {
    return false;
  }
}

const TAGS = new Set([
  "a",
  "b",
  "i",
  "em",
  "strong",
  "span",
  "br",
  "small",
  "sup",
  "sub",
]);
const ATTRS = new Set(["href", "title", "class", "target", "rel"]);

export const escapeHtml = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const escape = (s: string) =>
  s
    .replace(/&(?![#a-zA-Z0-9]+;)/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** Rebuilds an allowed tag with only safe attributes; null if not allowed. */
function tag(raw: string): string | null {
  const m =
    /^<(\/?)([a-zA-Z][a-zA-Z0-9]*)((?:\s+[a-zA-Z-]+\s*=\s*(?:"[^"<>]*"|'[^'<>]*'))*)\s*\/?>$/.exec(
      raw,
    );
  const name = m?.[2]?.toLowerCase();
  if (!m || !name || !TAGS.has(name)) return null;
  if (m[1]) return `</${name}>`;
  let out = `<${name}`;
  for (const a of (m[3] ?? "").matchAll(
    /([a-zA-Z-]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/g,
  )) {
    const key = a[1]?.toLowerCase() ?? "";
    const value = a[2] ?? a[3] ?? "";
    if (!ATTRS.has(key)) continue;
    if (key === "href" && !/^https?:\/\//i.test(value.trim())) continue;
    out += ` ${key}="${escape(value)}"`;
  }
  return `${out}>`;
}

/** Text, entities and allowed tags; anything else is made inert text. */
export function safeHtml(input: string): string {
  let out = "";
  let at = 0;
  for (const m of input.matchAll(/<[^<>]*>/g)) {
    out += escape(input.slice(at, m.index));
    out += tag(m[0]) ?? escape(m[0]);
    at = m.index + m[0].length;
  }
  return out + escape(input.slice(at));
}

const tt = (globalThis as { trustedTypes?: TrustedTypesFactory }).trustedTypes;
if (tt) {
  const page = window.location.href;
  tt.createPolicy("default", {
    createHTML: safeHtml,
    createScript: refuse("script"),
    createScriptURL: (url) => {
      if (scriptUrlAllowed(url, page)) return url;
      throw new TypeError(
        `Trusted Types: script URL ${url} is not this origin's`,
      );
    },
  });
}
